import dayjs from 'dayjs';
import { TimeGridEntryType } from '../../components/TimeGrid/graphql/types';
import { mergeConflictEntry } from '../../utils/timetable/availabilityConflicts';
import { BitStatus, StatusManager } from '../StatusManager';
import { roomSlotsOverlap, SubjectContainerSlot, teacherSlotsOverlap } from './SubjectContainerTypes';

type HH = string;
type mm = string;
type day = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export type Time = `${HH}:${mm}`; // 'HH:mm'
type TimeString = `${Time}${day}`; // 'HH:mm' + day
type ResourceUUID = string; // Klasse / DivisionGroupFormt / Lehrer / Raum UUID
type TimetableCardUUID = string;

// Uhrzeiten ohne dayjs in den Schleifen: der Store führt die Verfügbarkeit minutengenau. Beim Laden einer
// großen Version (~1.000 Karten, ~800 Verfügbarkeiten) liefen sonst Millionen dayjs-Parse/Format-Aufrufe
// (gemessen ~8 s von ~11 s Store-Aufbau). Ergebnis und Schlüssel bleiben dieselben wie mit dayjs.
const MINUTES_PER_DAY = 24 * 60;
const pad2 = (n: number) => (n < 10 ? `0${n}` : `${n}`);
const MINUTE_TIMES = Array.from({ length: MINUTES_PER_DAY }, (_, m) => `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`);
const normalizedTimes = new Map<string, Time>();

/** Wie `dayjs(time, 'HH:mm').format('HH:mm')`, je Eingabe nur einmal gerechnet. */
function toTime(time: string): Time {
  let normalized = normalizedTimes.get(time);
  if (normalized === undefined) {
    normalized = dayjs(time, 'HH:mm').format('HH:mm') as Time;
    normalizedTimes.set(time, normalized);
  }
  return normalized;
}

/** Die Uhrzeiten `start + i` Minuten für i = 0…count – wie `start.add(i, 'minute').format('HH:mm')`. */
function minuteTimes(start: dayjs.Dayjs, count: number): Time[] {
  const first = start.hour() * 60 + start.minute();
  const times: Time[] = [];
  for (let i = 0; i <= count; i++) {
    times.push(MINUTE_TIMES[(first + i) % MINUTES_PER_DAY] as Time);
  }
  return times;
}

type ClassUuid = string;
type DivisionUuid = string;
type GroupUuid = string;

export type DetailedPlacedInfo = {
  type: AvailabilityResourceType;
  uuid: string;
};

export type CheckCardAvailabilityResult = {
  status: AvailableStatus;
  placed?: DetailedPlacedInfo[];
  blocked?: DetailedPlacedInfo[];
};
export type CardClassDivisionGroupFormat = `${TimetableCardUUID}:${ClassUuid}:${DivisionUuid}:${GroupUuid}`;

export type AvailabilityResourceType = 'teacher' | 'class' | 'divisionGroup' | 'room' | 'subject';

export type CardAvailability = Map<
  ResourceUUID,
  {
    type: AvailabilityResourceType;
    maybe: boolean;
    blocked: boolean;
    placed: Set<TimetableCardUUID>;
  }
>;

export type Availability = Map<
  TimeString,
  Map<
    ResourceUUID,
    {
      type: AvailabilityResourceType;
      maybe: boolean;
      blocked: boolean;
      placed: Set<TimetableCardUUID>;
    }
  >
>;

export type AvailabilityCardType = {
  uuid: string;
  classes: string[];
  classDivisionGroups: CardClassDivisionGroupFormat[];
  teachers: string[];
  rooms: string[];
  subject: string;
};

export type Day = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export type Context = 'teachers' | 'classes' | 'divisionGroups' | 'rooms' | 'subjects';
export type AvailableStatus = 'blocked' | 'maybe' | 'placed' | 'available';

export class TimeAvailabilityStore {
  availabilities: Availability = new Map();
  days: Day[] = ['mon', 'tue', 'wed', 'thu', 'fri'];
  statusManager: ReturnType<typeof StatusManager>;
  timeGridEntries: TimeGridEntryType[] | null;

  constructor(from: string, to: string, days?: Day[], timeGridEntries?: TimeGridEntryType[]) {
    this.statusManager = StatusManager();
    this.timeGridEntries = timeGridEntries ?? null;

    const fromTime = dayjs(from, 'HH:mm');
    const untilTime = dayjs(to, 'HH:mm');
    const times = minuteTimes(fromTime, untilTime.diff(fromTime, 'minute'));
    if (days) {
      this.days = days;
    }

    for (const day of this.days) {
      for (const timeString of times) {
        this.availabilities.set(`${timeString}${day}`, new Map());
      }
    }
  }

  reset() {
    this.availabilities.forEach((value, key) => {
      this.availabilities.set(key, new Map());
    });
  }

  resetPlaced() {
    this.availabilities.forEach((slotMap) => {
      slotMap.forEach((availability, uuid) => {
        slotMap.set(uuid, { ...availability, placed: new Set() });
      });
    });
  }

  isResourcePlaced(time: string, day: Day, resourceUUID: ResourceUUID) {
    const timeString = toTime(time);
    const placedSize = this.availabilities.get(`${timeString}${day}`)?.get(resourceUUID)?.placed.size ?? 0;
    return placedSize > 0;
  }

  isResourceBlocked(time: string, day: Day, resourceUUID: ResourceUUID) {
    const timeString = toTime(time);
    return this.availabilities.get(`${timeString}${day}`)?.get(resourceUUID)?.blocked ?? false;
  }

  isResourceMaybe(time: string, day: Day, resourceUUID: ResourceUUID) {
    const timeString = toTime(time);
    return this.availabilities.get(`${timeString}${day}`)?.get(resourceUUID)?.maybe ?? false;
  }

  setResourcePlaced(
    time: string,
    day: Day,
    resourceUUID: ResourceUUID,
    cardUuid: string,
    type: AvailabilityResourceType,
  ) {
    const timeString = toTime(time);
    const timeAvailabilities = this.availabilities.get(`${timeString}${day}`);

    if (timeAvailabilities) {
      const currentBlocked = timeAvailabilities?.get(resourceUUID)?.blocked ?? false;
      const currentMaybe = timeAvailabilities?.get(resourceUUID)?.maybe ?? false;
      const currentPlaced = timeAvailabilities?.get(resourceUUID)?.placed ?? new Set();
      timeAvailabilities?.set(resourceUUID, {
        type: type,
        placed: currentPlaced.add(cardUuid),
        maybe: currentMaybe,
        blocked: currentBlocked,
      });
    }
  }

  removeResourcePlaced(
    time: string,
    day: Day,
    resourceUUID: ResourceUUID,
    cardUuid: string,
    type: AvailabilityResourceType,
  ) {
    const timeString = toTime(time);
    const timeAvailabilities = this.availabilities.get(`${timeString}${day}`);
    if (timeAvailabilities) {
      const currentBlocked = timeAvailabilities?.get(resourceUUID)?.blocked ?? false;
      const currentMaybe = timeAvailabilities?.get(resourceUUID)?.maybe ?? false;
      const currentPlaced = timeAvailabilities?.get(resourceUUID)?.placed ?? new Set();
      currentPlaced.delete(cardUuid);
      timeAvailabilities?.set(resourceUUID, {
        type: type,
        placed: currentPlaced,
        maybe: currentMaybe,
        blocked: currentBlocked,
      });
    }
  }

  setResourceBlocked(
    time: string,
    day: Day,
    resourceUUID: ResourceUUID,
    blocked: boolean,
    type: AvailabilityResourceType,
  ) {
    const timeString = toTime(time);
    const newAvailabilities = this.availabilities.get(`${timeString}${day}`);
    if (newAvailabilities) {
      const currentMaybe = newAvailabilities?.get(resourceUUID)?.maybe ?? false;
      const currentPlaced = newAvailabilities?.get(resourceUUID)?.placed ?? new Set();
      // blocked und maybe schließen sich aus: wer blockiert, hebt ein evtl. gesetztes maybe auf,
      // sonst gewinnt in Anzeige/Menü fälschlich das weiche maybe über das harte blocked.
      newAvailabilities?.set(resourceUUID, {
        type: type,
        placed: currentPlaced,
        maybe: blocked ? false : currentMaybe,
        blocked,
      });
    }
  }

  setResourceMaybe(time: string, day: Day, resourceUUID: ResourceUUID, maybe: boolean, type: AvailabilityResourceType) {
    const timeString = toTime(time);
    const newAvailabilities = this.availabilities.get(`${timeString}${day}`);
    if (newAvailabilities) {
      const currentBlocked = newAvailabilities?.get(resourceUUID)?.blocked ?? false;
      const currentPlaced = newAvailabilities?.get(resourceUUID)?.placed ?? new Set();
      // blocked und maybe schließen sich aus: wer maybe setzt, hebt ein evtl. gesetztes blocked auf.
      newAvailabilities?.set(resourceUUID, {
        type: type,
        placed: currentPlaced,
        maybe,
        blocked: maybe ? false : currentBlocked,
      });
    }
  }

  setResourceAvailable(time: string, day: Day, resourceUUID: ResourceUUID, type: AvailabilityResourceType) {
    const timeString = toTime(time);
    const newAvailabilities = this.availabilities.get(`${timeString}${day}`);
    if (newAvailabilities) {
      const currentPlaced = newAvailabilities?.get(resourceUUID)?.placed ?? new Set();

      newAvailabilities?.set(resourceUUID, {
        type: type,
        placed: currentPlaced,
        maybe: false,
        blocked: false,
      });
    }
  }

  checkResourceConflicts({
    uuid,
    day,
    times,
    rooms,
    teachers,
    classes,
    classDivisionGroups,
    subject,
    subjectContainerSlots,
    getSlotsForCard,
    isCoexistable = false,
    isCardCoexistable,
  }: {
    subject: string;
    uuid: string;
    times: Time[];
    day: Day;
    rooms: string[];
    teachers: string[];
    classes: string[];
    classDivisionGroups: string[];
    subjectContainerSlots?: SubjectContainerSlot[];
    getSlotsForCard?: (uuid: string) => SubjectContainerSlot[] | undefined;
    /** true, wenn die geprüfte Karte selbst koexistierbar (elective/attendance) ist */
    isCoexistable?: boolean;
    /** liefert für eine belegte Karte, ob sie koexistierbar ist */
    isCardCoexistable?: (uuid: string) => boolean;
  }): CardAvailability | null {
    const cardAvailability: CardAvailability = new Map();
    const classesSet = new Set(classes);
    // Überlappungen zwischen koexistierbaren Karten (elective/attendance) sind gewollt und
    // erzeugen keinen Konflikt. Verfügbarkeits-Konflikte (blocked/maybe) bleiben unberührt.
    const suppress = (cardUuid: string): boolean => isCoexistable && isCardCoexistable?.(cardUuid) === true;
    const mySlots = subjectContainerSlots && subjectContainerSlots.length > 0 ? subjectContainerSlots : undefined;

    times.forEach((time) => {
      const minAvailabilities = this.availabilities.get(`${time}${day}`);
      if (!minAvailabilities) return;

      // Direct O(1) lookups instead of iterating all entries for each resource type
      rooms.forEach((room) => {
        const minAvailability = minAvailabilities.get(room);
        if (!minAvailability) return;
        const placedWithoutCurrent = [...minAvailability.placed].filter((c) => {
          if (c === uuid) return false;
          if (suppress(c)) return false;
          if (mySlots && getSlotsForCard) {
            const otherSlots = getSlotsForCard(c);
            if (otherSlots && otherSlots.length > 0) {
              return roomSlotsOverlap(mySlots, otherSlots, room);
            }
          }
          return true;
        });
        if (
          minAvailability.type === 'room' &&
          (minAvailability.maybe || minAvailability.blocked || placedWithoutCurrent.length > 0)
        ) {
          mergeConflictEntry(cardAvailability, room, minAvailability);
        }
      });

      teachers.forEach((teacher) => {
        const minAvailability = minAvailabilities.get(teacher);
        if (!minAvailability) return;
        const placedWithoutCurrent = [...minAvailability.placed].filter((c) => {
          if (c === uuid) return false;
          if (suppress(c)) return false;
          if (mySlots && getSlotsForCard) {
            const otherSlots = getSlotsForCard(c);
            if (otherSlots && otherSlots.length > 0) {
              return teacherSlotsOverlap(mySlots, otherSlots, teacher);
            }
          }
          return true;
        });
        if (
          minAvailability.type === 'teacher' &&
          (minAvailability.maybe || minAvailability.blocked || placedWithoutCurrent.length > 0)
        ) {
          mergeConflictEntry(cardAvailability, teacher, minAvailability);
        }
      });

      classes.forEach((c) => {
        const minAvailability = minAvailabilities.get(c);
        if (!minAvailability) return;
        const placedWithoutCurrent = [...minAvailability.placed].filter((cd) => cd !== uuid && !suppress(cd));
        if (
          minAvailability.type === 'class' &&
          (minAvailability.maybe || minAvailability.blocked || placedWithoutCurrent.length > 0)
        ) {
          const current = cardAvailability.get(c);
          if (minAvailability.blocked) {
            cardAvailability.set(c, minAvailability);
          } else if (minAvailability?.maybe && !current?.blocked) {
            cardAvailability.set(c, minAvailability);
          }
        }
      });

      if (subject) {
        const minAvailability = minAvailabilities.get(subject);
        if (
          minAvailability &&
          (minAvailability.maybe || (minAvailability.type === 'subject' && minAvailability.blocked))
        ) {
          mergeConflictEntry(cardAvailability, subject, minAvailability);
        }
      }

      // Division groups use compound keys — still need iteration, but skip all non-compound keys
      if (classDivisionGroups.length > 0) {
        minAvailabilities.forEach((minAvailability, key) => {
          if (!key || !key.includes(':')) return;
          const [card, classUuid, divisionUuid, groupUuid] = key.split(':');
          if (card === uuid || !classesSet.has(classUuid)) return;
          if (suppress(card)) return;
          if (minAvailability.placed.size === 0) return;

          if (
            classDivisionGroups.some((cdg) => cdg.includes(divisionUuid)) &&
            classDivisionGroups.some((cdg) => cdg.includes(groupUuid))
          ) {
            cardAvailability.set(groupUuid, minAvailability);
          } else if (classDivisionGroups.every((cdg) => !cdg.includes(divisionUuid))) {
            cardAvailability.set(divisionUuid, minAvailability);
          }
        });
      }
    });

    return cardAvailability.size > 0 ? cardAvailability : null;
  }

  checkCardAvailability({
    day,
    time,
    card,
    context,
    ignoreCardUuids,
  }: {
    time: Time;
    day: Day;
    card: AvailabilityCardType;
    context: Context;
    /**
     * Gesteckte Karten, die bei der Prüfung nicht zählen – z. B. die Karte, mit der beim Ablegen
     * getauscht wird: sie verlässt den Slot, ein Konflikt nur mit ihr entsteht also nicht.
     */
    ignoreCardUuids?: ReadonlySet<string>;
  }): CheckCardAvailabilityResult {
    const minAvailabilities = this.availabilities.get(`${time}${day}`);
    const placedCards: DetailedPlacedInfo[] = [];
    if (minAvailabilities && minAvailabilities?.size > 0) {
      const counts = (c: string) => c !== card.uuid && !ignoreCardUuids?.has(c);
      const isBlocked = (uuid: string) => minAvailabilities.get(uuid)?.blocked ?? false;
      const isMaybe = (uuid: string) => minAvailabilities.get(uuid)?.maybe ?? false;
      const isPlaced = (uuid: string) => {
        const cards = minAvailabilities.get(uuid)?.placed;
        return cards ? [...cards].some(counts) : false;
      };

      const blockedCards: DetailedPlacedInfo[] = [];
      card.teachers.forEach((t) => {
        if (isBlocked(t)) blockedCards.push({ type: 'teacher', uuid: t });
      });
      card.classes.forEach((c) => {
        if (isBlocked(c)) blockedCards.push({ type: 'class', uuid: c });
      });
      card.classDivisionGroups.forEach((g) => {
        if (isBlocked(g)) blockedCards.push({ type: 'divisionGroup', uuid: g });
      });
      card.rooms.forEach((r) => {
        if (isBlocked(r)) blockedCards.push({ type: 'room', uuid: r });
      });
      if (minAvailabilities.get(card.subject)?.blocked) blockedCards.push({ type: 'subject', uuid: card.subject });

      if (blockedCards.length > 0) return { status: 'blocked', blocked: blockedCards };

      card.teachers.forEach((teacher) => {
        if (isPlaced(teacher)) placedCards.push({ type: 'teacher', uuid: teacher });
      });

      if (context !== 'classes') {
        card.classes.forEach((c) => {
          if (isPlaced(c)) placedCards.push({ type: 'class', uuid: c });
        });
      }

      card.rooms.forEach((r) => {
        if (isPlaced(r)) placedCards.push({ type: 'room', uuid: r });
      });

      let placed = placedCards.length > 0;

      if (context === 'classes' && !placed) {
        card.classDivisionGroups.forEach((g) => {
          const [, classUuid, divisionUuid, groupUuid] = g.split(':');
          if (divisionUuid == 'noDivision') {
            return;
          }
          minAvailabilities.forEach((value, key) => {
            const placedWithoutCurrent = [...value.placed].filter(counts);
            if (key && key.includes(':')) {
              // andere Teilung
              if (key.includes(classUuid) && !key.includes(divisionUuid) && placedWithoutCurrent.length > 0) {
                placed = true;
                placedCards.push({ type: 'divisionGroup', uuid: key });
              }
              // gleiche Teilung gleiche Gruppe
              if (
                key.includes(classUuid) &&
                key.includes(divisionUuid) &&
                key.includes(groupUuid) &&
                placedWithoutCurrent.length > 0
              ) {
                placed = true;
                placedCards.push({ type: 'divisionGroup', uuid: key });
              }
            }
          });
        });
      }
      if (placed) return { status: 'placed', placed: placedCards };

      const maybe =
        card.teachers.some(isMaybe) ||
        card.classes.some(isMaybe) ||
        card.classDivisionGroups.some(isMaybe) ||
        card.rooms.some(isMaybe) ||
        minAvailabilities.get(card.subject)?.maybe;

      if (maybe) return { status: 'maybe' };
    }
    return { status: 'available' };
  }

  setMaybeForRange(
    range: { start: dayjs.Dayjs; end: dayjs.Dayjs; day: Day },
    resourceUUID: ResourceUUID,
    maybe: boolean,
    type: AvailabilityResourceType,
  ) {
    const { start, end, day } = range;
    for (const time of minuteTimes(start, end.diff(start, 'minute'))) {
      this.setResourceMaybe(time, day, resourceUUID, maybe, type);
    }
  }

  setBlockedForRange(
    range: { start: dayjs.Dayjs; end: dayjs.Dayjs; day: Day },
    resourceUUID: ResourceUUID,
    blocked: boolean,
    type: AvailabilityResourceType,
  ) {
    const { start, end, day } = range;
    for (const time of minuteTimes(start, end.diff(start, 'minute'))) {
      this.setResourceBlocked(time, day, resourceUUID, blocked, type);
    }
  }

  setAvailableForRange(
    range: { start: string; end: string; day: Day },
    resourceUUID: ResourceUUID,
    type: AvailabilityResourceType,
  ) {
    const { start, end, day } = range;
    const startTime = dayjs(start);
    const endTime = dayjs(end);
    for (const time of minuteTimes(startTime, endTime.diff(startTime, 'minute'))) {
      this.setResourceAvailable(time, day, resourceUUID, type);
    }
  }

  setCardsResourcesPlaced(
    cards: AvailabilityCardType[],
    range: {
      start: Time;
      end: Time;
      day: Day;
    },
  ) {
    const { start, end, day } = range;
    const startTime = dayjs(start, 'HH:mm');
    const endTime = dayjs(end, 'HH:mm');
    const times = minuteTimes(startTime, endTime.diff(startTime, 'minute'));
    cards.forEach((card) => {
      for (const time of times) {
        card.teachers.forEach((teacher) => {
          this.setResourcePlaced(time, day, teacher, card.uuid, 'teacher');
        });
        card.classes.forEach((c) => {
          this.setResourcePlaced(time, day, c, card.uuid, 'class');
        });
        card.classDivisionGroups.forEach((g) => {
          this.setResourcePlaced(time, day, g, card.uuid, 'divisionGroup');
        });
        card.rooms.forEach((r) => {
          this.setResourcePlaced(time, day, r, card.uuid, 'room');
        });
      }
    });
  }

  removeCardsResourcesPlaced(
    cards: AvailabilityCardType[],
    range: {
      start: string;
      end: string;
      day: Day;
    },
  ) {
    const { start, end, day } = range;
    const startTime = dayjs(start, 'HH:mm');
    const endTime = dayjs(end, 'HH:mm');
    const times = minuteTimes(startTime, endTime.diff(startTime, 'minute'));

    cards.forEach((card) => {
      for (const time of times) {
        card.teachers.forEach((teacher) => {
          this.removeResourcePlaced(time, day, teacher, card.uuid, 'teacher');
        });
        card.classes.forEach((c) => {
          this.removeResourcePlaced(time, day, c, card.uuid, 'class');
        });
        card.classDivisionGroups.forEach((g) => {
          this.removeResourcePlaced(time, day, g, card.uuid, 'divisionGroup');
        });
        card.rooms.forEach((r) => {
          this.removeResourcePlaced(time, day, r, card.uuid, 'room');
        });
      }
    });
  }

  setResourceAvailability(
    availabilities: {
      uuid: string;
      days: {
        mon?: string | null;
        tue?: string | null;
        wed?: string | null;
        thu?: string | null;
        fri?: string | null;
        sat?: string | null;
        sun?: string | null;
      };
    }[],
    type: AvailabilityResourceType,
  ) {
    this.timeGridEntries?.length &&
      this.timeGridEntries
        .sort((a, b) => {
          return a.order - b.order;
        })
        .forEach((timeGridEntry) => {
          const { order, start, end } = timeGridEntry;
          const startTime = dayjs(start);
          const endTime = dayjs(end);
          const times = minuteTimes(startTime, endTime.diff(startTime, 'minute'));
          this.days.forEach((day) => {
            availabilities.forEach((availability) => {
              const { uuid, days } = availability;
              if (days) {
                const currentValue = days[day] ?? '';
                const currentBit = this.statusManager.isFree(this.statusManager.stringToBit(currentValue[order]))
                  ? BitStatus.FREE
                  : this.statusManager.isMaybe(this.statusManager.stringToBit(currentValue[order]))
                    ? BitStatus.MAYBE
                    : BitStatus.OCCUPIED;
                if (this.statusManager.isOccupied(currentBit)) {
                  for (const time of times) this.setResourceBlocked(time, day, uuid, true, type);
                }
                if (this.statusManager.isMaybe(currentBit)) {
                  for (const time of times) this.setResourceMaybe(time, day, uuid, true, type);
                }
                if (this.statusManager.isFree(currentBit)) {
                  for (const time of times) this.setResourceAvailable(time, day, uuid, type);
                }
              }
            });
          });
        });
  }
}
